import { splitReason } from "./voice";
import { Spinner, copyText, useApp, useConnection } from "./ui";

const KEYBOARD_TIP = "Or tap the microphone on your keyboard to dictate. That works everywhere, with no setup.";

/**
 * Get voice ready on a computer, from the phone: one button downloads the speech model there (about 150 MB, once), with progress.
 * Everything runs on the computer and the model comes straight to it, so nothing about what you say leaves your devices. When the
 * computer is an older Brocoded, or doesn't have the speech program, it says what to do instead.
 */
export function VoiceSetupPanel({ computerId }: { computerId: string }) {
  const { toast } = useApp();
  const { conn, snap, computer } = useConnection(computerId);
  const v = snap.voice;
  const name = computer?.label ?? "your computer";
  const ready = snap.state === "ready";
  const setup = v?.setup;
  const installing = setup?.stage === "installing";
  const running = !!v?.settingUp || installing || setup?.stage === "downloading" || setup?.stage === "checking";
  const failed = setup?.stage === "error" ? setup.message ?? "Setup didn't finish. Try again." : null;
  const split = v && !v.ready && v.reason ? splitReason(v.reason) : null;

  if (v == null) {
    return (
      <div className="voice-help" role="status">
        <p>
          <Spinner size={12} /> Checking {name}…
        </p>
      </div>
    );
  }
  if (v.ready) {
    return (
      <div className="voice-help" data-testid="voice-ready">
        <p>Voice is ready on {name}. Tap the microphone in the message box and speak. Your words are turned into text on your computer.</p>
      </div>
    );
  }
  if (v.unsupported) {
    return (
      <div className="voice-help">
        <p>Update Brocoded on {name} to use voice.</p>
        <p>{KEYBOARD_TIP}</p>
      </div>
    );
  }
  if (running) {
    const pct = setup?.percent ?? 0;
    return (
      <div className="voice-help" data-testid="voice-setting-up" role="status">
        <p>{installing ? `Installing the speech program on ${name}…` : setup?.stage === "checking" ? `Checking the speech model on ${name}…` : `Downloading the speech model to ${name}… ${pct}%`}</p>
        {!installing && (
          <div className="usage-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Speech model download">
            <i style={{ width: `${Math.max(2, pct)}%` }} />
          </div>
        )}
        <p>It keeps going on your computer if you close this. The first time takes a few minutes.</p>
      </div>
    );
  }
  // the one button covers both: the computer installs the speech program (a Mac with Homebrew) and downloads the model by itself
  const showSetup = v.needs === "model" || (v.needs === "program" && !failed);
  if (showSetup) {
    return (
      <div className="voice-help" data-testid="voice-setup">
        <p>Voice needs a one-time setup on {name}{v.needs === "program" ? ": the speech program and a speech model (about 150 MB)" : ": a speech model (about 150 MB)"}. We do it for you, and it runs there, so what you say never leaves your devices.</p>
        {failed && (
          <p className="err-text" role="alert">
            {failed}
          </p>
        )}
        <button className="btn primary" disabled={!ready} onClick={() => conn?.setupVoice()} data-testid="voice-setup-button">
          {failed ? "Try again" : `Set up voice on ${name}`}
        </button>
        <p>{KEYBOARD_TIP}</p>
      </div>
    );
  }
  // the computer couldn't install the speech program by itself (not a Mac with Homebrew): the command is the fallback
  return (
    <div className="voice-help">
      {failed && <p className="err-text" role="alert">{failed}</p>}
      <p>{split?.lead ?? "Voice isn't set up on your computer yet."} Your words are turned into text on your computer, so nothing about them leaves your devices.</p>
      {split?.command && <code>{split.command}</code>}
      {split?.command && (
        <button className="btn secondary sm" onClick={async () => toast((await copyText(split.command!)) ? "Copied" : "Couldn't copy")}>
          Copy command
        </button>
      )}
      {failed && (
        <button className="btn primary" disabled={!ready} onClick={() => conn?.setupVoice()}>
          Try again
        </button>
      )}
      <p>{KEYBOARD_TIP}</p>
    </div>
  );
}
